<template>
    <div v-if="user != null && !userLoading" class="user-detail-container">
        <!-- Cover Image Section -->
        <div class="cover-image-container" :style="coverBackgroundStyle">
            <div class="cover-overlay"></div>
            <div class="change-cover-field" v-if="isOwner">
                <div class="upload-button" v-if="!coverUploading">
                    <input type="file" @change="onCoverChanged" accept="image/*" hidden id="cover-file" />
                    <label for="cover-file" class="btn btn_primary cover-upload-btn">
                        <vue-feather type="camera" class="icon-upload" size="16" />
                        <span>Change Cover</span>
                    </label>
                </div>
                <div class="upload-status" v-if="coverUploading">
                    <vue-feather type="loader" animation="spin" size="16" />
                    <span>Uploading {{ coverUploadProgress ? `${Math.round(coverUploadProgress)}%` : '' }}</span>
                </div>
            </div>
        </div>

        <!-- User Header Section -->
        <div class="user-header">
            <div class="profile-section">
                <div class="profile-image-container">
                    <div class="user-profile-image" v-if="isOwner">
                        <img :src="avatarURL" :alt="displayName(user)" @error="handleAvatarError" />
                        <input type="file" @change="onProfileImageChanged" accept="image/*" hidden id="avatar-file" />
                        <label for="avatar-file" class="profile-upload-overlay" :class="{ visible: avatarUploading }">
                            <vue-feather v-if="avatarUploading" type="loader" animation="spin" size="22" />
                            <vue-feather v-else type="camera" class="icon-upload" size="20" />
                            <span>{{ avatarUploading ? `Uploading ${avatarUploadProgress ? Math.round(avatarUploadProgress) + '%' : ''}` : 'Change' }}</span>
                        </label>
                    </div>
                    <div class="user-profile-image" v-else>
                        <img :src="avatarURL" :alt="displayName(user)" @error="handleAvatarError" />
                    </div>
                </div>
                
                <div class="user-info">
                    <div class="user-name-section">
                        <h1 class="user-name">
                            {{ decodeHtml(displayName(user)) }}
                            <vue-feather type="check-circle" class="verified-badge" v-if="user.status === 'verified'" />
                        </h1>
                        <div class="user-stats">
                            <span class="username">@{{username}}</span>
                            <span class="stat-divider">•</span>
                            <span class="subscriber-count">{{ user.subscribersCount }} subscriber{{ user.subscribersCount !== 1 ? 's' : '' }}</span>
                            <span class="stat-divider">•</span>
                            <span class="video-count">{{ userVideos.length }}{{ videosNextToken ? '+' : '' }} video{{ userVideos.length !== 1 ? 's' : '' }}</span>
                        </div>
                        <p class="user-bio" v-if="user.bio">{{ decodeHtml(user.bio) }}</p>
                    </div>
                </div>
            </div>

            <div class="action-section">
                <div class="subscribe-action" v-if="!isOwner">
                    <button :disabled="loading" @click="subscribeToggle" class="btn btn_primary subscribe-btn">
                        <vue-feather type="user-plus" size="16" v-if="subscription == null" />
                        <vue-feather type="user-minus" size="16" v-else />
                        {{ subscription != null ? 'Unsubscribe' : 'Subscribe' }}
                    </button>
                    <button
                        v-if="subscription"
                        class="notification-btn"
                        :class="{ active: subscription.notify }"
                        @click="toggleNotify"
                        :title="subscription.notify ? 'Notifications on' : 'Notifications off'"
                    >
                        <vue-feather :type="subscription.notify ? 'bell' : 'bell-off'" size="18" />
                    </button>
                    <router-link
                        class="message-btn"
                        :to="`/messages/${user.id}`"
                        title="Send a message"
                    >
                        <vue-feather type="mail" size="18" />
                    </router-link>
                </div>
                <div v-if="isOwner">
                    <router-link :to="`/settings`" class="btn btn_primary edit-profile-btn">
                        <vue-feather type="edit-3" size="16" />
                        Edit Profile
                    </router-link>
                </div>
            </div>
        </div>

        <!-- Navigation Tabs -->
        <div class="navigation-tabs">
            <nav class="tab-nav">
                <router-link :to="`/@${username}`" :class="{'tab-link': true, 'active': currentTab == 'home'}">
                    Videos
                    <span class="tab-count">({{ userVideos.length }}{{ videosNextToken ? '+' : '' }})</span>
                </router-link>
                <router-link :to="`/@${username}/liked`" :class="{'tab-link': true, 'active': currentTab == 'liked'}">
                    Liked videos
                    <span class="tab-count">({{ likedVideos.length }})</span>
                </router-link>
                <router-link :to="`/@${username}/about`" :class="{'tab-link': true, 'active': currentTab == 'about'}">
                    About
                </router-link>
                <router-link :to="`/@${username}/store`" :class="{'tab-link': true, 'active': currentTab == 'store'}">
                    Store
                </router-link>
            </nav>
        </div>

        <!-- Content Sections -->
        <div class="content-section">
            <!-- About Tab -->
            <div class="about-tab" v-if="currentTab == 'about'">
                <div class="about-card">
                    <h3 class="about-title">About</h3>
                    <div class="about-info">
                        <div class="info-item" v-if="user.bio">
                            <label>Bio</label>
                            <p>{{ decodeHtml(user.bio) }}</p>
                        </div>
                        <div class="info-item">
                            <label>Country</label>
                            <p>{{ countryLabel }}</p>
                        </div>
                        <div class="info-item">
                            <label>Joined</label>
                            <p>{{ formattedCreatedAt }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Store Tab -->
            <div class="store-tab" v-if="currentTab == 'store'">
                <div class="coming-soon-card">
                    <vue-feather type="shopping-bag" size="48" class="coming-soon-icon" />
                    <h3>Store Coming Soon</h3>
                    <p>We're working on something exciting. Stay tuned!</p>
                </div>
            </div>

            <!-- Videos Tab -->
            <div v-if="currentTab == 'home'" class="videos-section">
                <div class="video-grid" v-if="userVideos.length > 0">
                    <div v-for="video in userVideos" :key="video.id" class="video-card" :class="{ 'is-processing': !isPlayableVideo(video), 'is-failed': isFailedVideo(video) }">
                        <router-link :to="videoLink(video)" :event="videoLinkEvent(video)" class="video-thumbnail-link" :class="{ 'is-editable-draft': isOwner && !isPlayableVideo(video) }">
                            <div class="video-thumbnail">
                                <img
                                    :src="imageURL(video)"
                                    :alt="getVideoTitle(video)"
                                    @error="handleVideoThumbnailError"
                                />
                                <div class="video-duration" v-if="isPlayableVideo(video)">{{ formattedDuration(video.duration) }}</div>
                                <span class="video-draft-chip" v-if="video.publishState && video.publishState !== 'published'">{{ publishStateLabel(video) }}</span>
                                <!-- Processing overlay -->
                                <div class="video-processing-overlay" v-if="!isPlayableVideo(video)">
                                    <vue-feather v-if="isFailedVideo(video)" type="alert-triangle" size="30" stroke-width="2.2" class="video-processing-icon failed"></vue-feather>
                                    <vue-feather v-else type="loader" size="28" animation="spin" class="video-processing-icon"></vue-feather>
                                    <span class="video-processing-label">{{ videoStatusLabel(video) }}</span>
                                    <span class="video-processing-estimate" v-if="video.status === 'processing' && video.duration">{{ estimateTime(video.duration) }}</span>
                                </div>
                            </div>
                        </router-link>
                        <div class="video-info">
                            <router-link :to="videoLink(video)" :event="videoLinkEvent(video)" class="video-title">
                                {{ getVideoTitle(video) }}
                            </router-link>
                            <div class="video-meta">
                                <span class="processing-status-badge" v-if="!isPlayableVideo(video)">
                                    <vue-feather v-if="video.status === 'processing'" type="loader" size="12" animation="spin"></vue-feather>
                                    <vue-feather v-else-if="isFailedVideo(video)" type="alert-triangle" size="12"></vue-feather>
                                    {{ videoStatusDetail(video) }}
                                </span>
                                <template v-else>
                                    <span class="views-count">{{ video.viewsCount || 0 }} views</span>
                                    <span class="time-ago" v-if="video.createdAt">{{ timeAgo(Date.parse(video.createdAt)) }} ago</span>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="videosNextToken" class="load-more-container">
                    <button @click="loadMoreVideos()" :disabled="loadingMoreVideos" class="load-more-button">
                        <vue-feather v-if="loadingMoreVideos" type="loader" animation="spin" size="16"></vue-feather>
                        {{ loadingMoreVideos ? 'Loading...' : 'Load More Videos' }}
                    </button>
                </div>
                <div v-if="userVideos.length === 0 && !userLoading" class="no-videos-message">
                    <vue-feather type="video-off" size="32" stroke-width="1.5"></vue-feather>
                    <p>No videos yet</p>
                </div>
            </div>

            <!-- Liked Videos Tab -->
            <div v-if="currentTab == 'liked'" class="videos-section">
                <div class="video-grid">
                    <div v-for="videoItem in likedVideos" :key="videoItem.video.id" class="video-card">
                        <router-link :to="`/watch/${videoItem.video.id}`" class="video-thumbnail-link">
                            <div class="video-thumbnail">
                                <img
                                    :src="imageURL(videoItem.video)"
                                    :alt="getVideoTitle(videoItem.video)"
                                    @error="handleVideoThumbnailError"
                                />
                                <div class="video-duration">{{ formattedDuration(videoItem.video.duration) }}</div>
                            </div>
                        </router-link>
                        <div class="video-info">
                            <router-link :to="`/watch/${videoItem.video.id}`" class="video-title">
                                {{ getVideoTitle(videoItem.video) }}
                            </router-link>
                            <div class="video-meta">
                                <span class="views-count">{{ videoItem.video.viewsCount || 0 }} views</span>
                                <span class="time-ago" v-if="videoItem.video.createdAt">{{ timeAgo(Date.parse(videoItem.video.createdAt)) }} ago</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Loading and Error States -->
    <div v-if="!userLoading && user == null" style="display: flex; flex-direction: column; align-items: center; padding: 40px 20px; text-align: center; max-width: 600px; margin: 0 auto;">
        <vue-feather type="user-x" size="48" style="color: var(--text-muted); margin-bottom: 24px;" />
        <h3 style="font-size: 20px; font-weight: 600; color: var(--text-secondary); margin: 0 0 12px 0;">{{ currentLanguage === 'TA' ? 'பயனர் கிடைக்கவில்லை' : 'User Not Found' }}</h3>
        <p style="font-size: 16px; color: var(--text-tertiary); margin: 0 0 24px 0; line-height: 1.5;">{{ currentLanguage === 'TA' ? 'பயனர்பெயர் கிடைக்கவில்லை' : 'No user found with username' }} <strong>@{{ username }}</strong></p>
        <router-link to="/" class="btn btn_primary" style="display: inline-flex; align-items: center; gap: 8px; text-decoration: none; font-size: 14px; font-weight: 600; padding: 10px 20px; border-radius: 20px; color: white; background-color: var(--accent-primary);">
            <vue-feather type="home" size="16" />
            {{ currentLanguage === 'TA' ? 'முகப்புக்குச் செல்' : 'Go Home' }}
        </router-link>
    </div>
    
    <!-- Loading Shimmer State -->
    <div class="user-shimmer-container" v-if="userLoading">
        <!-- Cover shimmer -->
        <div class="shimmer-cover">
            <div class="shimmer-overlay"></div>
        </div>
        <!-- Profile header shimmer -->
        <div class="shimmer-profile-header">
            <div class="shimmer-profile-section">
                <div class="shimmer-profile-avatar"><div class="shimmer-overlay"></div></div>
                <div class="shimmer-profile-info">
                    <div class="shimmer-block" style="height: 28px; width: 200px;"><div class="shimmer-overlay"></div></div>
                    <div class="shimmer-block" style="height: 16px; width: 300px; margin-top: 10px;"><div class="shimmer-overlay"></div></div>
                    <div class="shimmer-block" style="height: 14px; width: 250px; margin-top: 8px;"><div class="shimmer-overlay"></div></div>
                </div>
            </div>
        </div>
        <!-- Tabs shimmer -->
        <div class="shimmer-tabs">
            <div class="shimmer-block pill" v-for="n in 4" :key="'shimmer-tab-' + n" style="height: 16px; width: 80px;"><div class="shimmer-overlay"></div></div>
        </div>
        <!-- Video grid shimmer -->
        <div class="shimmer-profile-content">
            <div class="shimmer-video-grid">
                <div v-for="n in 8" :key="'shimmer-uv-' + n" class="shimmer-video-card">
                    <div class="shimmer-thumb"><div class="shimmer-overlay"></div></div>
                    <div class="shimmer-card-info">
                        <div class="shimmer-card-text">
                            <div class="shimmer-card-title"><div class="shimmer-overlay"></div></div>
                            <div class="shimmer-card-title short"><div class="shimmer-overlay"></div></div>
                            <div class="shimmer-card-meta"><div class="shimmer-overlay"></div></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { API, Storage } from 'aws-amplify';
import { track as analyticsTrack } from '../utils/analytics';
import { getUserByUsername, videosByOwnerId, videosByOwnerIdAll, getUserModelByLegacyId, checkSubscription, interactionByUserId } from '../graphql/custom'
import { createUserSubscriber, deleteUserSubscriber, updateUserSubscriber, updateUserModel } from '../graphql/mutations'

export default {
    name: 'App',
    components: {
        //   'v-icon': Icon
    },
    inject: {
        isAdminRef: { from: 'isAdminRef', default: null }
    },
    data() {
        return {
            currentUser: null,
            currentUserModel: null,
            user: null,
            uploading: false,
            coverUploading: false,
            avatarUploading: false,
            loading: false,
            uploaded: false,
            subscription: null,
            uploadProgress: null,
            coverUploadProgress: 0,
            avatarUploadProgress: 0,
            userLoading: true,
            coverURL: '',
            coverFallbackURL: '/cover-placeholder.svg',
            avatarURL: '',
            likedVideoItems: [],
            userVideos: [],
            videosNextToken: null,
            loadingMoreVideos: false,
            videosPagesLoaded: 0,
            videosOwnerId: null
        }
    },
    async created() {
        await this.loadPageData();
    },
    watch: {
        // Admin state resolves asynchronously after page load; refetch so
        // unpublished videos appear once the viewer is known to be an admin.
        isAdminViewer(isAdmin) {
            if (isAdmin && !this.isOwner && this.user) this.fetchUserVideos();
        },
        // Watch the entire route object for changes
        '$route': {
            async handler(to, from) {
                // Check if we're still on a user profile page and username changed
                if (to.params.username && to.params.username !== from.params.username) {
                    console.log('Route changed - Username:', from.params.username, '->', to.params.username);
                    // Reset state
                    this.user = null;
                    this.subscription = null;
                    this.coverURL = '';
                    this.avatarURL = '';
                    this.likedVideoItems = [];
                    this.userVideos = [];
                    this.videosNextToken = null;
                    this.videosOwnerId = null;
                    this.videosPagesLoaded = 0;
                    this.loadingMoreVideos = false;
                    this.userLoading = true;
                    // Reload all page data
                    await this.loadPageData();
                }
            },
            deep: true
        }
    },
    computed: {
        isAdminViewer() {
            // unwrapInjectedRef is on (main.js), so the injected ref arrives unwrapped.
            // Named isAdminViewer because the global mixin's `isAdmin` data would shadow it.
            return this.isAdminRef === true;
        },
        canSeeAllVideos() {
            return this.isOwner || this.isAdminViewer;
        },
        currentTab() {
            // Use route param directly - empty/undefined means home tab
            return this.$route.params.tab || 'home'
        },
        username() {
            return this.$route.params.username
        },
        coverBackgroundStyle() {
            const images = [];
            if (this.coverURL) {
                images.push(`url("${this.coverURL}")`);
            }
            images.push(`url("${this.coverFallbackURL}")`);
            return {
                backgroundImage: images.join(', ')
            };
        },
        isOwner() {
            if (!this.user || !this.currentUser) return false;
            const legacyUserId = this.currentUser['custom:legacyUserId'];
            // Check multiple ways to match ownership:
            // 1. DynamoDB ID matches Cognito sub
            // 2. DynamoDB ID matches legacy user ID
            // 3. DynamoDB legacyId matches Cognito legacy user ID
            // 4. Usernames match
            return this.user.id === this.currentUser.sub ||
                   this.user.id === legacyUserId ||
                   (this.user.legacyId && this.user.legacyId === legacyUserId) ||
                   (this.currentUser.username && this.user.username === this.currentUser.username);
        },
        formattedCreatedAt() {
            return new Date(this.user.createdAt).toLocaleDateString("en-US")
        },
        countryLabel() {
            let userCountry = this.user.country
            let foundCountries = this.countries.filter (country => country.value == userCountry)
            if (foundCountries.length > 0) {
                return foundCountries[0].label
            } else {
                return "-"
            }
        },
        likedVideos() {
            return this.likedVideoItems.filter(item =>
                item.video && item.video.id && this.isPublicVideo(item.video)
            );
        }
    },
    methods: {
        videoPaginationKey() {
            return `profile-video-pages:${this.currentUser?.sub || 'guest'}:${this.user.id}`;
        },
        rememberedVideoPages() {
            try {
                const pages = Number(sessionStorage.getItem(this.videoPaginationKey()));
                return Number.isSafeInteger(pages) && pages > 0 ? pages : 1;
            } catch {
                return 1;
            }
        },
        rememberVideoPages() {
            try {
                sessionStorage.setItem(this.videoPaginationKey(), String(this.videosPagesLoaded));
            } catch {
                // Browsing still works when session storage is unavailable.
            }
        },
        isPlayableVideo(video) {
            return !video?.status || ['ready', 'processingReady'].includes(video.status);
        },
        videoLink(video) {
            if (!video?.id) return '';
            if (this.isPlayableVideo(video)) return `/watch/${video.id}`;
            // Owner can open their own processing/failed video to edit its details.
            return this.isOwner ? `/finish/${video.id}` : '';
        },
        videoLinkEvent(video) {
            return this.videoLink(video) ? 'click' : '';
        },
        isFailedVideo(video) {
            return video?.status === 'failed';
        },
        videoStatusLabel(video) {
            if (this.isFailedVideo(video)) return 'Failed';
            if (video?.status === 'processing') return 'Processing...';
            return video?.status || 'Processing';
        },
        videoStatusDetail(video) {
            if (this.isFailedVideo(video)) return this.isOwner ? 'Processing failed · Tap to edit' : 'Processing failed';
            if (video?.status === 'processing') {
                const base = `Converting video... ${video.duration ? this.estimateTime(video.duration) : ''}`.trim();
                return this.isOwner ? `${base} · Tap to edit` : base;
            }
            return video?.status || 'Processing';
        },
        isPublicVideo(video) {
            return video &&
                (!video.publishState || video.publishState === 'published') &&
                this.isPlayableVideo(video);
        },
        publishStateLabel(video) {
            const labels = { draft: 'Draft', pending: 'Pending', private: 'Private', rejected: 'Rejected' };
            return labels[video.publishState] || video.publishState;
        },
        async loadPageData() {
            this.userLoading = true;
            // Run both fetches in parallel, then check subscription status
            await Promise.all([
                this.fetchCurrentUser(),
                this.fetchUserDetails()
            ]);
            if (this.user) {
                await this.fetchUserVideos();
            }
            // Check subscription after both user models are loaded
            await this.checkSubscriptionStatus();
            this.userLoading = false;
            // Fetch liked videos separately (uses GSI with large limit to avoid pagination filtering issue)
            if (this.user) {
                this.fetchLikedVideos();
            }
        },
        isMenuOn(item) {
            return this.$route.path == item
        },
        estimateTime(duration) {
            const durationMin = duration / 60;
            const estMinutes = Math.ceil(2 + durationMin * 2.5);
            if (estMinutes < 60) return `~${estMinutes} min`;
            const hours = Math.floor(estMinutes / 60);
            const mins = estMinutes % 60;
            return mins > 0 ? `~${hours}h ${mins}m` : `~${hours}h`;
        },
        imageURL(video) {
            if (!video?.id) return '/video-placeholder.svg';
            // Use thumbnailUrl from legacy data if available and not empty
            if (video.thumbnailUrl && video.thumbnailUrl.trim()) {
                if (video.thumbnailUrl.includes('https://')) {
                    return video.thumbnailUrl;
                }
                return `https://cdn.eelam.tv/${video.thumbnailUrl}`;
            }
            // Fallback to CDN URL using imageName (selected thumbnail index)
            const imageIndex = video.imageName || '0';
            return `https://cdn1.eelam.tv/public/thumbs/${video.id}_${imageIndex}.jpeg`;
        },
        handleVideoThumbnailError(event) {
            if (event.target.src.endsWith('/video-placeholder.svg')) return;
            event.target.src = '/video-placeholder.svg';
        },
        async fetchLikedVideos() {
            try {
                let allLikes = [];
                let nextToken = null;
                // Paginate through all interactions, filtering for likes
                do {
                    const res = await API.graphql({
                        query: interactionByUserId,
                        variables: {
                            userId: this.user.id,
                            sortDirection: 'DESC',
                            filter: { type: { eq: 'like' } },
                            limit: 500,
                            nextToken: nextToken
                        }
                    });
                    const items = res.data.interactionByUserId.items || [];
                    allLikes.push(...items);
                    nextToken = res.data.interactionByUserId.nextToken;
                } while (nextToken);
                this.likedVideoItems = allLikes;
                console.log('Fetched liked videos:', allLikes.length);
            } catch (error) {
                console.error('Error fetching liked videos:', error);
            }
        },
        async fetchCurrentUser() {
            try {
                let user = await API.Auth.currentAuthenticatedUser()
                console.log('Current User Info', user)
                this.currentUser = user.attributes
                this.currentUser.username = user.attributes.preferred_username || user.attributes['custom:legacyUsername'] || user.username

                // Fetch user model to get subscriptions
                // First try by legacyId (for migrated users)
                const legacyUserId = this.currentUser['custom:legacyUserId'];
                if (legacyUserId) {
                    const res = await API.graphql({
                        query: getUserModelByLegacyId,
                        variables: { legacyId: legacyUserId }
                    });
                    if (res.data.userByLegacyId?.items?.length > 0) {
                        this.currentUserModel = res.data.userByLegacyId.items[0];
                        console.log('Current user model with subscriptions:', this.currentUserModel);
                    }
                }

                // If not found by legacyId, try by username
                if (!this.currentUserModel && this.currentUser.username) {
                    const res = await API.graphql({
                        query: getUserByUsername,
                        variables: { username: this.currentUser.username }
                    });
                    if (res.data.userByUsername?.items?.length > 0) {
                        this.currentUserModel = res.data.userByUsername.items[0];
                        console.log('Current user model found by username:', this.currentUserModel);
                    }
                }
            } catch (error) {
                console.log('User not authenticated');
            }
        },
        async fetchUserDetails() {
            this.userLoading = true
            const username = this.$route.params.username
            const userInput = {
                username
            };
            const videoRes = await API.graphql({
                query: getUserByUsername,
                variables: userInput,
            });
            console.log('user: ', userInput, videoRes)
            this.user = videoRes.data.userByUsername.items[0] || null;

            if (!this.user) {
                document.title = 'User Not Found - Eelam TV';
                return;
            }

            // Update page title
            const displayName = this.decodeHtml(this.displayName(this.user))
            document.title = `${displayName} - Eelam TV`

            this.downloadProfileImage(this.user.id)
            this.downloadCoverImage(this.user.id)
        },
        async checkSubscriptionStatus() {
            if (!this.currentUserModel || !this.user) {
                this.subscription = null;
                return;
            }
            // Query UserSubscriber using GSI on subscriberId + subscribedToId
            try {
                const res = await API.graphql({
                    query: checkSubscription,
                    variables: {
                        subscriberId: this.currentUserModel.id,
                        subscribedToId: { eq: this.user.id }
                    }
                });
                const items = res.data.subscribersBySubscriberId?.items || [];
                this.subscription = items.length > 0 ? items[0] : null;
                console.log('Subscription status:', this.subscription);
            } catch (error) {
                console.error('Error checking subscription status:', error);
                this.subscription = null;
            }
        },
        async fetchUserVideos() {
            if (!this.user) return;
            const profileId = this.user.id;
            try {
                // Use the "all" query (includes processing/failed) when viewing own profile
                const query = this.canSeeAllVideos ? videosByOwnerIdAll : videosByOwnerId;
                // Determine the ownerId to query with
                let ownerId = this.user.id;
                let result = await this.fetchVisibleVideosPage(query, ownerId);
                if (this.user?.id !== profileId) return;
                let videos = result.visibleVideos;
                let nextToken = result.nextToken;

                // If no videos found and user has legacyId, try generated UUID
                if (!videos.length && this.user.legacyId) {
                    const generatedUuid = await this.generateUserUuid(this.user.legacyId);
                    console.log('Trying generated UUID for legacyId:', this.user.legacyId, '->', generatedUuid);
                    result = await this.fetchVisibleVideosPage(query, generatedUuid);
                    if (this.user?.id !== profileId) return;
                    videos = result.visibleVideos;
                    nextToken = result.nextToken;
                    if (videos.length) ownerId = generatedUuid;
                }

                this.videosOwnerId = ownerId;
                this.userVideos = videos;
                this.videosNextToken = nextToken;
                this.videosPagesLoaded = 1;
                const pagesToRestore = this.rememberedVideoPages();
                while (this.user?.id === profileId && this.videosNextToken && this.videosPagesLoaded < pagesToRestore) {
                    const loaded = await this.loadMoreVideos(false);
                    if (!loaded) break;
                }
                console.log('Fetched user videos:', videos.length, 'hasMore:', !!nextToken);
            } catch (error) {
                console.error('Error fetching user videos:', error);
                if (this.user?.id !== profileId) return;
                this.userVideos = [];
                this.videosNextToken = null;
            }
        },
        async fetchVisibleVideosPage(query, ownerId, nextToken = null) {
            let token = nextToken;
            const visibleVideos = [];
            let pagesChecked = 0;

            do {
                const variables = {
                    ownerId,
                    limit: 100
                };
                if (token) {
                    variables.nextToken = token;
                }
                const videosRes = await API.graphql({
                    query,
                    variables
                });
                const result = videosRes.data.videosByOwner;
                const pageVideos = result.items || [];
                visibleVideos.push(...(this.canSeeAllVideos ? pageVideos : pageVideos.filter(video => this.isPublicVideo(video))));
                token = result.nextToken || null;
                pagesChecked += 1;
            } while (token && visibleVideos.length === 0 && pagesChecked < 10);

            return {
                visibleVideos,
                nextToken: token
            };
        },
        async loadMoreVideos(remember = true) {
            if (!this.videosNextToken || this.loadingMoreVideos) return;
            const profileId = this.user.id;
            this.loadingMoreVideos = true;
            try {
                const query = this.canSeeAllVideos ? videosByOwnerIdAll : videosByOwnerId;
                const result = await this.fetchVisibleVideosPage(query, this.videosOwnerId, this.videosNextToken);
                if (this.user?.id !== profileId) return false;
                const visibleVideos = result.visibleVideos;
                this.userVideos = [...this.userVideos, ...visibleVideos];
                this.videosNextToken = result.nextToken;
                this.videosPagesLoaded += 1;
                if (remember) this.rememberVideoPages();
                console.log('Loaded more videos:', visibleVideos.length, 'total:', this.userVideos.length);
                return true;
            } catch (error) {
                console.error('Error loading more videos:', error);
                return false;
            } finally {
                if (this.user?.id === profileId) this.loadingMoreVideos = false;
            }
        },
        resizeImage(file, maxWidth) {
            return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                    const scale = Math.min(1, maxWidth / img.width);
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width * scale;
                    canvas.height = img.height * scale;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    canvas.toBlob((blob) => resolve(blob), 'image/png', 0.9);
                };
                img.src = URL.createObjectURL(file);
            });
        },
        async onCoverChanged(e) {
            const file = e.target.files[0];
            if (!file) return;
            analyticsTrack('profile_image_upload_start', {
                type: 'cover',
                file_size: file?.size,
                file_type: file?.type
            });
            console.log('on change fired', file, this.currentUser);

            this.coverUploading = true
            this.coverUploadProgress = 0
            try {
                const resized = await this.resizeImage(file, 1200);
                const ts = Date.now();
                const s3Key = `images/user/${this.user.id}/cover_${ts}.png`;
                await Storage.put(s3Key, resized, {
                    level: 'public',
                    contentType: 'image/png',
                    progressCallback: (progress) => {
                        this.coverUploadProgress = (progress.loaded / progress.total) * 100
                    }
                });
                console.log('Successfully uploaded cover:', s3Key);
                // Store versioned path in legacyCover
                const cdnPath = `public/${s3Key}`;
                try {
                    await API.graphql({
                        query: updateUserModel,
                        variables: { input: { id: this.user.id, legacyCover: cdnPath } },
                        authMode: 'AMAZON_COGNITO_USER_POOLS'
                    });
                    this.user.legacyCover = cdnPath;
                } catch (err) {
                    console.error('Failed to update user model:', err);
                    analyticsTrack('profile_image_upload_failed', {
                        type: 'cover',
                        stage: 'update_user_model',
                        error_code: this.getAnalyticsErrorCode(err)
                    });
                }
                // Use local blob for immediate preview
                this.coverURL = URL.createObjectURL(resized);
                analyticsTrack('profile_image_change', { type: 'cover' });
            } catch (err) {
                console.error('Unexpected error while uploading', err);
                analyticsTrack('profile_image_upload_failed', {
                    type: 'cover',
                    stage: 'storage_put',
                    error_code: this.getAnalyticsErrorCode(err)
                });
                this.$toast.error('Failed to upload cover image.');
            } finally {
                this.coverUploading = false
                this.coverUploadProgress = 0
                e.target.value = ''
            }
        },
        async onProfileImageChanged(e) {
            const file = e.target.files[0];
            if (!file) return;
            analyticsTrack('profile_image_upload_start', {
                type: 'avatar',
                file_size: file?.size,
                file_type: file?.type
            });
            console.log('on change fired', file);

            this.avatarUploading = true
            this.avatarUploadProgress = 0
            try {
                const resized = await this.resizeImage(file, 400);
                const ts = Date.now();
                const s3Key = `images/user/${this.user.id}/avatar_${ts}.png`;
                await Storage.put(s3Key, resized, {
                    level: 'public',
                    contentType: 'image/png',
                    progressCallback: (progress) => {
                        this.avatarUploadProgress = (progress.loaded / progress.total) * 100
                    }
                });
                console.log('Successfully uploaded avatar:', s3Key);
                // Store versioned path in legacyAvatar
                const cdnPath = `public/${s3Key}`;
                try {
                    await API.graphql({
                        query: updateUserModel,
                        variables: { input: { id: this.user.id, legacyAvatar: cdnPath } },
                        authMode: 'AMAZON_COGNITO_USER_POOLS'
                    });
                    this.user.legacyAvatar = cdnPath;
                } catch (err) {
                    console.error('Failed to update user model:', err);
                    analyticsTrack('profile_image_upload_failed', {
                        type: 'avatar',
                        stage: 'update_user_model',
                        error_code: this.getAnalyticsErrorCode(err)
                    });
                }
                // Use local blob for immediate preview
                this.avatarURL = URL.createObjectURL(resized);
                analyticsTrack('profile_image_change', { type: 'avatar' });
            } catch (err) {
                console.error('Unexpected error while uploading', err);
                analyticsTrack('profile_image_upload_failed', {
                    type: 'avatar',
                    stage: 'storage_put',
                    error_code: this.getAnalyticsErrorCode(err)
                });
                this.$toast.error('Failed to upload profile image.');
            } finally {
                this.avatarUploading = false
                this.avatarUploadProgress = 0
                e.target.value = ''
            }
        },
        getAnalyticsErrorCode(error) {
            const message = String(error?.errors?.[0]?.message || error?.message || error?.name || '').toLowerCase();
            if (!message) return 'unknown';
            if (message.includes('not authorized') || message.includes('unauthorized')) return 'unauthorized';
            if (message.includes('network') || message.includes('fetch')) return 'network_error';
            if (message.includes('timeout') || message.includes('timed out')) return 'timeout';
            if (message.includes('validation')) return 'validation_error';
            if (message.includes('storage') || message.includes('s3')) return 'storage_error';
            return 'error';
        },
        downloadProfileImage(userId) {
            if (this.user?.legacyAvatar) {
                if (this.user.legacyAvatar.startsWith('public/')) {
                    this.avatarURL = `https://cdn1.eelam.tv/${this.user.legacyAvatar}`;
                } else {
                    this.avatarURL = `https://cdn.eelam.tv/${this.user.legacyAvatar}`;
                }
                return;
            }
            this.avatarURL = `https://cdn1.eelam.tv/public/images/user/${userId}/avatar.png`;
        },
        handleAvatarError() {
            this.avatarURL = '/avatar-placeholder.svg';
        },
        downloadCoverImage(userId) {
            if (this.user?.legacyCover) {
                if (this.user.legacyCover.startsWith('public/')) {
                    this.coverURL = `https://cdn1.eelam.tv/${this.user.legacyCover}`;
                } else {
                    this.coverURL = `https://cdn.eelam.tv/${this.user.legacyCover}`;
                }
                return;
            }
            this.coverURL = `https://cdn1.eelam.tv/public/images/user/${userId}/cover.png`;
        },
        displayName(user) {
            const fullName = [user?.firstName, user?.lastName]
                .filter(Boolean)
                .join(' ')
                .trim();
            return fullName || user?.displayName || user?.username || 'Unknown User'
        },
        getVideoTitle(video) {
            if (!video) return 'Untitled';
            // Get language preference from localStorage
            const lang = localStorage.getItem('preferredLanguage') || 'EN';
            if (lang === 'TA') {
                return this.decodeHtml(video.titleTA || video.titleEN) || 'Untitled';
            }
            return this.decodeHtml(video.titleEN || video.titleTA) || 'Untitled';
        },
        formattedDuration(duration) {
            if (!duration) return '0:00'
            const minutes = Math.floor(duration / 60)
            const seconds = duration % 60
            return `${minutes}:${seconds.toString().padStart(2, '0')}`
        },
        timeAgo(date) {
            const now = new Date()
            const diffInSeconds = Math.floor((now - date) / 1000)
            
            if (diffInSeconds < 60) return `${diffInSeconds} second${diffInSeconds !== 1 ? 's' : ''}`
            
            const diffInMinutes = Math.floor(diffInSeconds / 60)
            if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''}`
            
            const diffInHours = Math.floor(diffInMinutes / 60)
            if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''}`
            
            const diffInDays = Math.floor(diffInHours / 24)
            if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''}`
            
            const diffInWeeks = Math.floor(diffInDays / 7)
            if (diffInWeeks < 4) return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''}`
            
            const diffInMonths = Math.floor(diffInDays / 30)
            if (diffInMonths < 12) return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''}`
            
            const diffInYears = Math.floor(diffInDays / 365)
            return `${diffInYears} year${diffInYears !== 1 ? 's' : ''}`
        },
        async generateUserUuid(legacyId) {
            // Generate UUID5 matching Python's uuid.uuid5(uuid.NAMESPACE_DNS, "eelam.tv.user.{legacyId}")
            // UUID5 uses SHA-1 hash of namespace + name
            const { v5: uuidv5 } = await import('uuid');
            const DNS_NAMESPACE = '6ba7b810-9dad-11d1-80b4-00c04fd430c8';
            return uuidv5(`eelam.tv.user.${legacyId}`, DNS_NAMESPACE);
        },
        async toggleNotify() {
            if (!this.subscription) return;
            try {
                const newNotify = !this.subscription.notify;
                await API.graphql({
                    query: updateUserSubscriber,
                    variables: {
                        input: { id: this.subscription.id, notify: newNotify }
                    },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                this.subscription.notify = newNotify;
                analyticsTrack('notification_toggle', { channel_id: this.user.id, enabled: newNotify });
            } catch (error) {
                console.error('Error toggling notify:', error);
                this.$toast.error('Failed to update notification settings.');
            }
        },
        async subscribeToggle() {
            if (!this.currentUserModel || !this.user) {
                console.error('Cannot subscribe: user not logged in or channel not loaded');
                this.$toast.error('Please log in to subscribe.');
                return;
            }

            this.loading = true;
            try {
                if (this.subscription) {
                    // Unsubscribe
                    analyticsTrack('channel_unsubscribe', { channel_id: this.user.id });
                    console.log('Unsubscribing from:', this.user.username);
                    await API.graphql({
                        query: deleteUserSubscriber,
                        variables: {
                            input: { id: this.subscription.id }
                        },
                        authMode: 'AMAZON_COGNITO_USER_POOLS'
                    });
                    this.subscription = null;
                    // Update local subscribersCount
                    if (this.user.subscribersCount > 0) {
                        this.user.subscribersCount--;
                    }
                } else {
                    // Subscribe
                    analyticsTrack('channel_subscribe', { channel_id: this.user.id, channel_username: this.user.username });
                    console.log('Subscribing to:', this.user.username);
                    const result = await API.graphql({
                        query: createUserSubscriber,
                        variables: {
                            input: {
                                subscribedToId: this.user.id,
                                subscriberId: this.currentUserModel.id,
                                notify: true
                            }
                        },
                        authMode: 'AMAZON_COGNITO_USER_POOLS'
                    });
                    this.subscription = result.data.createUserSubscriber;
                    console.log('Subscription created:', this.subscription);
                    // Update local subscribersCount
                    this.user.subscribersCount = (this.user.subscribersCount || 0) + 1;
                }
            } catch (error) {
                console.error('Error toggling subscription:', error);
                this.$toast.error('Failed to update subscription. Please try again.');
            } finally {
                this.loading = false;
            }
        }
    },
}
</script>

<style scoped>
/* Main Container */
.user-detail-container {
    max-width: 1200px;
    margin: 0 auto;
    background: var(--bg-card);
    border-radius: 12px;
    box-shadow: 0 2px 8px var(--shadow-color);
    overflow: hidden;
}

/* Cover Image Section */
.cover-image-container {
    position: relative;
    height: 300px;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    display: flex;
    align-items: flex-end;
}

.cover-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(180deg, transparent 0%, transparent 50%, var(--bg-secondary) 100%);
}

.change-cover-field {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 10;
}

.cover-upload-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: 138px;
    line-height: 1;
    background: rgba(255, 255, 255, 0.94);
    color: #3f3f3f;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(0, 0, 0, 0.12);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
}

.cover-upload-btn:hover {
    background: #4c4c4c;
    color: #fff !important;
    transform: translateY(-1px);
}

.cover-upload-btn svg {
    flex: 0 0 auto;
}

.upload-status {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 138px;
    justify-content: center;
    background: rgba(255, 255, 255, 0.94);
    color: #3f3f3f;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 700;
    backdrop-filter: blur(10px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
}

/* User Header Section */
.user-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding: 0 40px 30px;
    margin-top: -80px;
    position: relative;
    z-index: 5;
}

.profile-section {
    display: flex;
    align-items: flex-end;
    gap: 24px;
}

.profile-image-container {
    position: relative;
}

.user-profile-image {
    width: 160px;
    height: 160px;
    border-radius: 50%;
    background-color: var(--bg-tertiary);
    border: 4px solid var(--bg-card);
    box-shadow: 0 4px 12px var(--shadow-color);
    position: relative;
    overflow: hidden;
}

.user-profile-image img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
}

.profile-upload-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: white;
    font-size: 12px;
    font-weight: 500;
    opacity: 0;
    transition: opacity 0.2s ease;
    cursor: pointer;
    text-align: center;
}

.user-profile-image:hover .profile-upload-overlay,
.profile-upload-overlay.visible {
    opacity: 1;
}

.user-info {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;
}

.user-name-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.user-name {
    font-size: 32px;
    font-weight: 700;
    color: var(--text-secondary);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
}

.verified-badge {
    color: #1da1f2;
    stroke-width: 2;
}

.user-stats {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    color: var(--text-tertiary);
}

.username {
    font-weight: 600;
    color: var(--text-secondary);
}

.stat-divider {
    color: var(--text-muted);
}

.subscriber-count,
.video-count {
    font-weight: 500;
}

.user-bio {
    font-size: 16px;
    line-height: 1.5;
    color: var(--text-tertiary);
    margin: 0;
    max-width: 500px;
}

/* Action Section */
.action-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: flex-end;
}

.subscribe-action {
    display: flex;
    align-items: center;
    gap: 12px;
}

.subscribe-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 25px;
    transition: all 0.2s ease;
}

.subscribe-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.notification-btn {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 2px solid var(--border-color);
    background: var(--bg-card);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: var(--text-tertiary);
}

.notification-btn:hover {
    border-color: var(--text-link-hover);
    color: var(--text-link-hover);
    transform: scale(1.05);
}

.notification-btn.active {
    border-color: var(--accent-primary);
    color: white;
    background: var(--accent-primary);
}

.message-btn {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 2px solid var(--border-color);
    background: var(--bg-card);
    display: flex !important;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: var(--text-tertiary) !important;
    text-decoration: none !important;
}

.message-btn:hover {
    border-color: var(--text-link-hover);
    color: var(--text-link-hover) !important;
    transform: scale(1.05);
}

.edit-profile-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 25px;
}

/* Navigation Tabs */
.navigation-tabs {
    border-bottom: 1px solid var(--border-color);
    background: var(--bg-tertiary);
}

.tab-nav {
    display: flex;
    padding: 0 40px;
    gap: 8px;
}

.tab-link {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 16px 20px;
    text-decoration: none;
    color: var(--text-tertiary);
    font-weight: 500;
    font-size: 16px;
    border-bottom: 3px solid transparent;
    transition: all 0.2s ease;
    position: relative;
}

.tab-link:hover {
    color: var(--text-link-hover);
    background: var(--bg-hover);
}

.tab-link.active {
    color: var(--text-link-hover);
    border-bottom-color: var(--text-link-hover);
    background: var(--bg-hover);
}

.tab-count {
    font-size: 14px;
    color: var(--text-muted);
    font-weight: 400;
}

.tab-link.active .tab-count {
    color: var(--text-link-hover);
}

/* Content Section */
.content-section {
    padding: 40px;
}

/* About Tab */
.about-card {
    background: var(--bg-card);
    border-radius: 12px;
    padding: 32px;
    max-width: 600px;
    box-shadow: 0 2px 8px var(--shadow-color);
}

.about-title {
    font-size: 24px;
    font-weight: 700;
    color: var(--text-secondary);
    margin: 0 0 24px 0;
}

.about-info {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.info-item label {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.info-item p {
    font-size: 16px;
    color: var(--text-secondary);
    margin: 0;
    line-height: 1.5;
}

/* Store Tab */
.coming-soon-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 80px 40px;
    background: var(--bg-card);
    border-radius: 12px;
    max-width: 400px;
    margin: 0 auto;
    box-shadow: 0 2px 8px var(--shadow-color);
}

.coming-soon-icon {
    color: var(--text-muted);
    margin-bottom: 24px;
}

.coming-soon-card h3 {
    font-size: 24px;
    font-weight: 700;
    color: var(--text-secondary);
    margin: 0 0 12px 0;
}

.coming-soon-card p {
    font-size: 16px;
    color: var(--text-tertiary);
    margin: 0;
    line-height: 1.5;
}

/* Videos Section */
.videos-section {
    margin-top: 20px;
}

.load-more-container {
    text-align: center;
    margin-top: 32px;
}

.load-more-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--accent-primary, #ff1314);
    color: white;
    border: none;
    padding: 12px 28px;
    border-radius: 25px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.load-more-button:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.load-more-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.no-videos-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 60px 20px;
    color: var(--text-tertiary);
    text-align: center;
}

.no-videos-message p {
    margin: 0;
    font-size: 16px;
}

.video-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 24px;
}

.video-card {
    background: var(--bg-card);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px var(--shadow-color);
    transition: all 0.2s ease;
}

.video-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px var(--shadow-dropdown);
}

.video-thumbnail-link {
    display: block;
    position: relative;
    overflow: hidden;
}

.video-thumbnail {
    width: 100%;
    height: 160px;
    background: var(--bg-tertiary);
    position: relative;
    transition: transform 0.2s ease;
    overflow: hidden;
}

.video-thumbnail img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
}

.video-card:hover .video-thumbnail {
    transform: scale(1.05);
}

.video-draft-chip {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 3;
    background: #6b7280;
    color: #fff;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
}

.video-duration {
    position: absolute;
    bottom: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
}

/* Processing state */
.video-card.is-processing {
    opacity: 0.8;
}

.video-card.is-processing.is-failed {
    opacity: 1;
}

.video-card.is-processing .video-thumbnail-link {
    cursor: default;
}

/* Owner's own processing/failed videos are clickable (they open the edit page). */
.video-card.is-processing .video-thumbnail-link.is-editable-draft,
.video-thumbnail-link.is-editable-draft {
    cursor: pointer;
}

.video-card.is-processing:hover .video-thumbnail {
    transform: none;
}

.video-card.is-processing .video-thumbnail {
    background: var(--bg-tertiary);
}

.video-processing-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.video-card.is-failed .video-processing-overlay {
    background: rgba(35, 8, 8, 0.72);
}

.video-processing-icon {
    color: white;
    opacity: 0.9;
}

.video-processing-icon.failed {
    color: #ffb3a8;
}

.video-processing-label {
    color: white;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.5px;
    opacity: 0.9;
}

.video-processing-estimate {
    color: white;
    font-size: 11px;
    opacity: 0.7;
}

.processing-status-badge {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    color: var(--accent-primary);
    font-weight: 500;
}

.video-card.is-failed .processing-status-badge {
    color: #f04438;
}

.video-card.is-processing .video-title {
    cursor: default;
}

.video-card.is-processing .video-title:hover {
    color: var(--text-secondary);
}

.video-info {
    padding: 16px;
}

.video-title {
    display: block;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-secondary);
    text-decoration: none;
    line-height: 1.4;
    margin-bottom: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.video-title:hover {
    color: var(--text-link-hover);
}

.video-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: var(--text-tertiary);
}

.views-count,
.time-ago {
    font-weight: 500;
}

/* User profile shimmer skeleton */
.user-shimmer-container {
    max-width: 1200px;
    margin: 0 auto;
    background: var(--bg-card);
    border-radius: 12px;
    box-shadow: 0 2px 8px var(--shadow-color);
    overflow: hidden;
}

.shimmer-cover {
    height: 300px;
    background: var(--bg-tertiary);
    position: relative;
    overflow: hidden;
}

.shimmer-profile-header {
    padding: 0 40px 30px;
    margin-top: -80px;
    position: relative;
    z-index: 5;
}

.shimmer-profile-section {
    display: flex;
    align-items: flex-end;
    gap: 24px;
}

.shimmer-profile-avatar {
    width: 160px;
    height: 160px;
    border-radius: 50%;
    background: var(--bg-tertiary);
    border: 4px solid var(--bg-card);
    flex-shrink: 0;
    overflow: hidden;
    position: relative;
}

.shimmer-profile-info {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
}

.shimmer-tabs {
    display: flex;
    gap: 20px;
    padding: 16px 40px;
    border-bottom: 1px solid var(--border-color);
    background: var(--bg-tertiary);
}

.shimmer-profile-content {
    padding: 40px;
}

.shimmer-video-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 24px;
}

/* No avatar column for user profile cards */
.shimmer-profile-content .shimmer-card-info {
    padding: 12px 0;
}

@media (max-width: 768px) {
    .shimmer-profile-header {
        padding: 0 20px 30px;
    }

    .shimmer-profile-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }

    .shimmer-profile-avatar {
        width: 120px;
        height: 120px;
    }

    .shimmer-tabs {
        padding: 12px 20px;
        overflow-x: auto;
    }

    .shimmer-profile-content {
        padding: 20px;
    }

    .shimmer-video-grid {
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 16px;
    }
}

@media (max-width: 480px) {
    .shimmer-cover {
        height: 200px;
    }

    .shimmer-profile-header {
        margin-top: -60px;
    }

    .shimmer-profile-avatar {
        width: 100px;
        height: 100px;
    }

    .shimmer-video-grid {
        grid-template-columns: 1fr;
    }
}

/* Loading and Error States */
.user-not-found {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 20px;
    text-align: center;
}

.error-icon {
    color: var(--text-muted);
    margin-bottom: 24px;
}

.user-not-found h3 {
    font-size: 20px;
    font-weight: 600;
    color: var(--text-secondary);
    margin: 0 0 12px 0;
}

.user-not-found p {
    font-size: 16px;
    color: var(--text-tertiary);
    margin: 0 0 24px 0;
    line-height: 1.5;
}

.go-home-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    font-size: 14px;
    font-weight: 600;
    padding: 10px 20px;
    border-radius: 20px;
    color: white;
    background-color: var(--accent-primary);
    transition: all 0.2s ease;
}

.go-home-btn:hover {
    background-color: var(--accent-hover);
    transform: translateY(-1px);
}

/* Responsive Design */
@media (max-width: 768px) {
    .user-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 24px;
        padding: 0 20px 30px;
    }

    .profile-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }

    .user-profile-image {
        width: 120px;
        height: 120px;
    }

    .user-name {
        font-size: 24px;
    }

    .action-section {
        align-items: flex-start;
        width: 100%;
    }

    .subscribe-action {
        width: 100%;
        justify-content: space-between;
    }

    .tab-nav {
        padding: 0 20px;
        overflow-x: auto;
        gap: 4px;
    }

    .tab-link {
        padding: 12px 16px;
        font-size: 14px;
        white-space: nowrap;
    }

    .content-section {
        padding: 20px;
    }

    .video-grid {
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 16px;
    }

    .about-card,
    .coming-soon-card {
        padding: 24px;
    }
}

@media (max-width: 480px) {
    .cover-image-container {
        height: 200px;
    }

    .user-header {
        margin-top: -60px;
    }

    .user-profile-image {
        width: 100px;
        height: 100px;
    }

    .user-name {
        font-size: 20px;
    }

    .user-stats {
        font-size: 14px;
    }

    .video-grid {
        grid-template-columns: 1fr;
    }
}
</style>
